/**
 * v2 shared types. All Phase 2 modules (parse, detect, template, mapping,
 * variants, images, export) consume these. Keep this file stable — changes
 * here ripple across every v2 lib module.
 *
 * Design doc: docs/V2-PLAN.md
 */

// Structured slash-native template values. A templated field accepts either the
// new segment array or a legacy `{{ }}` string (parsed on read) — additive, so
// old Profile JSON keeps type-checking and resolving.
import type { TemplateValue } from "@/modules/_shared/templateValue";
export type { TemplateValue } from "@/modules/_shared/templateValue";


/** A value that may be templated: structured segments or a legacy string. */
export type Templatable = string | TemplateValue;

// ---------------------------------------------------------------------------
// Product types we know how to detect / map. Anything else => "Unknown".
// ---------------------------------------------------------------------------
export const PRODUCT_TYPES = ["Saree", "Lehenga", "Suit", "Kurta", "Generic", "Unknown"] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

// ---------------------------------------------------------------------------
// Parsed workbook shape (output of src/lib/mapper/parse.ts).
// Stored verbatim in Workbook.sheets (JSON).
// ---------------------------------------------------------------------------
export type ParsedSheet = {
  name: string;
  /** @deprecated derived alias of importConfig.headerRowStart — kept for back-compat readers. */
  headerRowIdx: number;
  /** @deprecated derived alias of importConfig.dataStartIdx — kept for back-compat readers. */
  dataStartIdx: number;
  /** Header cell values in column order. May contain duplicates; mapper UI handles disambiguation. */
  headers: string[];
  /** Total number of data rows (excluding header). */
  rowCount: number;
  /** First N data rows as objects keyed by header. Used by the workbook overview page. */
  sample: SourceRow[];
  /** Source of truth for structure. Optional on read for legacy rows (see ensureImportConfig). */
  importConfig?: ImportConfig;
};

export type ParsedWorkbook = {
  sheets: ParsedSheet[];
};

/**
 * Per-sheet import configuration — the source of truth for a sheet's structure.
 * Indices are 0-based into the raw grid (array-of-arrays from the parser).
 */
export type ImportConfig = {
  /** First header row (inclusive). */
  headerRowStart: number;
  /** Last header row (inclusive). Equals headerRowStart for a single-row header. */
  headerRowEnd: number;
  /** First data row. */
  dataStartIdx: number;
  /** Absolute raw-row indices to drop from the data region. */
  skippedRowIdxs: number[];
  /** Raw column indices to drop entirely. */
  excludedColIdxs: number[];
  /** rawColIdx -> custom header name (wins over the flattened name). */
  headerOverrides: Record<number, string>;
  /** Separator used when joining grouped header rows. */
  joinSeparator: string;
};

/** A merged-cell range, matching the shape of `XLSX.WorkSheet["!merges"]`. */
export type MergeRange = { s: { r: number; c: number }; e: { r: number; c: number } };

// ---------------------------------------------------------------------------
// A row from the source xlsx, keyed by header name.
// Cell values are whatever xlsx parsed them as (string | number | boolean | Date | "").
// ---------------------------------------------------------------------------
export type SourceCell = string | number | boolean | Date | null;
export type SourceRow = Record<string, SourceCell>;

// ---------------------------------------------------------------------------
// Editable workbook overlay.
// Mirrors the backend shape, but is owned locally by the frontend package.
// ---------------------------------------------------------------------------
export type ManualColumnDef = { name: string; kind: "manual" };

export type WorkbookOverlay = {
  rowOrder: string[];
  addedRows: Record<string, Record<string, string>>;
  overrides: Record<string, Record<string, string>>;
  addedColumns: ManualColumnDef[];
  /**
   * AI output per base row id → column. Stored once generated (like a value
   * in a spreadsheet cell) so it survives other columns being generated, rule
   * edits and reloads, and the export reuses it instead of calling the AI.
   */
  generated?: Record<string, Record<string, string>>;
  /** The sheet's own saved rules ("Save workbook"); wins over the template. */
  profile?: Profile | null;
};

export function emptyOverlay(): WorkbookOverlay {
  return {
    rowOrder: [],
    addedRows: {},
    overrides: {},
    addedColumns: [],
  };
}

// ---------------------------------------------------------------------------
// Detection result.
// ---------------------------------------------------------------------------
export type Detection = {
  type: ProductType;
  /** 0..1 — how confident the rule was. 1.0 = exact match, <0.6 = treat as guess. */
  confidence: number;
};

// ---------------------------------------------------------------------------
// Mapping Profile DSL — the heart of v2. See docs/V2-PLAN.md §2.
// ---------------------------------------------------------------------------

/**
 * Supported AI providers.
 *
 * - "anthropic" / "openai" — cloud APIs via the official SDKs. Need an API key
 *   stored in `v2.ai.<provider>.apiKey` Setting. Reasoning supported.
 * - "claude-local" — runs the locally installed, logged-in Claude Code CLI
 *   (`claude -p`) so output is billed against the user's Claude subscription.
 *   Only works when the backend runs on the user's own machine with
 *   AI_LOCAL_CLI=true; hosted deployments reject it.
 * - "openrouter" — one key for many vendors (DeepSeek, Qwen, Gemini, GLM, …)
 *   through OpenRouter's OpenAI-compatible API, plus TypeSafe's Jev decision
 *   model (see AIDecision). Key in `v2.ai.openrouter.apiKey`.
 */
export type AIProvider = "anthropic" | "openai" | "claude-local" | "openrouter";

/** One candidate answer for a Jev decision (a choice option or a tag). */
export type AIDecisionOption = {
  /** Text written to the cell when this option is picked / this tag applies. */
  value: string;
  /** What the option means, or the yes/no question for a tag. Optional. */
  description?: string;
};

/**
 * A typed question for Jev (TypeSafe's decision model on OpenRouter). Jev
 * returns probabilities instead of prose, so the cell value is computed from
 * the answer — never parsed out of free text.
 *
 * - "choice": pick exactly one option. Below `minConfidence` → `fallback`.
 * - "tags":   one yes/no question per tag; tags whose probability reaches
 *             `threshold` (default 0.5) are joined with `separator` (", ").
 * - "yes-no": one yes/no question; writes `yes` or `no`.
 */
export type AIDecision =
  | {
      kind: "choice";
      question: string;
      options: AIDecisionOption[];
      /** 0..1. Answers with a lower confidence write `fallback`. Default 0. */
      minConfidence?: number;
      fallback?: string;
    }
  | {
      kind: "tags";
      tags: AIDecisionOption[];
      /** 0..1 probability a tag needs to be written. Default 0.5. */
      threshold?: number;
      separator?: string;
    }
  | {
      kind: "yes-no";
      question: string;
      yes: string;
      no: string;
      /** 0..1 probability of "yes" needed to write `yes`. Default 0.5. */
      threshold?: number;
    };

/**
 * Reasoning / extended-thinking configuration. Per-rule, not per-provider —
 * users select model + effort independently per AI rule so different fields
 * can use different intensities.
 *
 * - "off": skip the reasoning param entirely (works on every model).
 * - "openai-effort": OpenAI Responses/Chat API `reasoning_effort`.
 *   Accepted values vary by model — see ai-models.ts.
 * - "anthropic-adaptive": modern Claude 4.6+ `thinking: { type: "adaptive" }`.
 *   Lets the model decide budget. Recommended default for thinking models.
 * - "anthropic-budget": legacy `thinking: { type: "enabled", budget_tokens }`.
 *   Min 1024, must be < maxTokens. Kept for older Claude versions and
 *   power users who want a fixed budget.
 */
export type AIReasoning =
  | { kind: "off" }
  | { kind: "openai-effort"; effort: "minimal" | "low" | "medium" | "high" | "xhigh" }
  | { kind: "anthropic-adaptive" }
  | { kind: "anthropic-budget"; budgetTokens: number };

export type Predicate = {
  /** A source header, or "out:Field" to read another output cell. */
  column: string;
  op:
    | "equals"
    | "notEquals"
    | "contains"
    | "notContains"
    | "startsWith"
    | "endsWith"
    | "matches"
    | "isEmpty"
    | "isNotEmpty"
    | "gt"
    | "lt";
  value?: Templatable;
  /** Defaults to false. */
  caseSensitive?: boolean;
  /**
   * Per-condition joiner, applied left-to-right against the running result of
   * the preceding conditions. The FIRST condition's `join` is ignored.
   *
   * When ANY predicate in a group carries an explicit `join`, the group is
   * evaluated as a sequential left-fold honouring each condition's joiner.
   * When NO predicate carries a `join` (legacy configs), the group falls back
   * to the old all-AND (`{ all }`) / all-OR (`{ any }`) behaviour.
   */
  join?: "and" | "or";
};

export type Condition = { all: Predicate[] } | { any: Predicate[] };
export type ConditionalBranch = { when: Condition; then: Rule };

/** A single find→replace operation used by the "find-replace" cell module. */
export type FindReplaceOp = {
  /** The substring (or regex source when `regex` is true) to look for. */
  find: string;
  /** Replacement text. (`$1` etc. work when `regex` is true.) */
  replace: string;
  /** Treat `find` as a regular expression. Defaults to false (literal). */
  regex?: boolean;
  /** Match case-insensitively. Defaults to false. */
  caseInsensitive?: boolean;
};

/** A numeric component within a price layer. `expr` is a formula evaluated by
 *  expression.ts after {{Field}}/{{out:Field}}/{{subtotal}} substitution. */
export type PriceAmount = { expr: Templatable };

/** One if/else branch of a price layer. Reuses the shared Condition model. */
export type PriceBranch = { when: Condition; amount: PriceAmount };

/** A stacked group of if/else branches that contributes one summed component. */
export type PriceLayer = {
  /** Display-only label, e.g. "Base", "GST", "Rounding". */
  label?: string;
  /** First matching branch wins. */
  branches: PriceBranch[];
  /** Amount used when no branch matches. Defaults to 0 when absent. */
  else?: PriceAmount;
};

/** A single field rule. */
export type Rule =
  | { kind: "source"; column: string }
  | { kind: "static"; value: Templatable }
  | { kind: "template"; value: Templatable }
  | { kind: "computed"; fn: string; args?: (string | number | TemplateValue)[] }
  | {
      kind: "dictionary";
      /** Input: a templated value (source/workbook field refs + text). */
      input: Templatable;
      /** Dictionary slug to look the value up in. */
      dictionary: string;
      /** What to emit when the value isn't in the dictionary. Default "passthrough". */
      onMiss?: "passthrough" | "default";
      /** Used when onMiss === "default". */
      default?: string;
    }
  | {
      kind: "expression";
      /** Safe formula with field refs substituted before eval, e.g. (/Variant Price * 1.18) + 500 */
      expr: Templatable;
    }
  | {
      kind: "find-replace";
      /** Input: a templated value (source/workbook field refs + text). */
      input: Templatable;
      /** Ordered list of find→replace operations, applied top-to-bottom. */
      rules: FindReplaceOp[];
    }
  | {
      kind: "conditional";
      /** Evaluated top-to-bottom; first matching branch wins. */
      branches: ConditionalBranch[];
      /** Used when nothing matches. Defaults to empty string when absent. */
      else?: Rule;
    }
  | {
      kind: "ai";
      provider: AIProvider;
      /** Provider-specific model id (e.g., "claude-sonnet-4-6", "gpt-5.5"). */
      model: string;
      /** Prompt template with field-ref interpolation against the source row. */
      prompt: Templatable;
      /** Per-rule reasoning/thinking config. Defaults to { kind: "off" } when absent. */
      reasoning?: AIReasoning;
      /** Optional max output tokens; provider default if absent. */
      maxTokens?: number;
      /** Optional system/instruction. Sent as a system prompt where the provider supports it. */
      system?: Templatable;
      /** Post-process the answer. "slug" turns it into a URL handle (smart slugify). */
      format?: "text" | "slug";
      /**
       * When set, this is a Jev decision rule (provider "openrouter", a
       * `typesafe/jev-*` model): `prompt` is the product info Jev reads and the
       * cell value comes from the typed answer. See AIDecision.
       */
      decision?: AIDecision;
    }
  | {
      kind: "dropbox-image";
      /** Input: a templated value holding a Dropbox share URL. */
      input: Templatable;
    }
  | {
      kind: "price-engine";
      /** Evaluated top-to-bottom; the final price is the sum of every layer. */
      layers: PriceLayer[];
      /** Whole-number rounding of the summed total. Default "none". */
      rounding?: "none" | "nearest" | "up" | "down";
      /** Displayed decimal places. Default 2. */
      decimals?: number;
    }
  | {
      kind: "javascript";
      /** User JS body. `row` (file columns) and `out` (processed columns) are
       *  injected; `return` the cell value. Runs sandboxed (no I/O). */
      code: string;
    };

/** Legacy per-variant override. Kept for back-compat (normalizeVariants upgrades it). */
export type VariantOption = {
  value: string;
  priceDelta: number;
  skuSuffix: string;
};

/** Price adjustment for a layered variant value. */
export type VariantPriceAdj = {
  /** "fixed" adds `amount` (may be negative); "percent" adds amount% of the running value. */
  mode: "fixed" | "percent";
  amount: number;
};

/** One value within a variant dimension. */
export type VariantValue = {
  value: string;
  /** Applied to price-like applyTo columns. Absent = no change. */
  priceAdj?: VariantPriceAdj;
  /** Appended to SKU-like applyTo columns. Absent = "". */
  skuSuffix?: string;
};

/** One variant dimension -> a Shopify Option{n} Name + its values. */
export type VariantDimension = {
  /** Shopify Option{n} Name (e.g. "Stitching", "Size"). */
  name: string;
  values: VariantValue[];
};

export type VariantConfig = {
  enabled: boolean;
  /** NEW: layered dimensions (1..3). When present, takes priority over the legacy fields. */
  dimensions?: VariantDimension[];
  /** LEGACY: single-dimension option name. Upgraded into dimensions[0] by normalizeVariants. */
  optionName?: string;
  /** LEGACY: single-dimension options. Upgraded into dimensions[0].values. */
  options?: VariantOption[];
  /**
   * Shopify column names variant rules apply to. Price-like columns get the
   * price adjustment; SKU-like columns get the suffix. Default set picked by
   * mapping.ts if omitted.
   */
  applyTo?: string[];
};

/**
 * One product image, in position order. `src` is usually a single source
 * column (a chip), but may add text around it — e.g. a CDN prefix + a file-name
 * column. A cell holding several URLs (separated by new lines, or by commas /
 * spaces before the next "http") yields several images.
 */
export type ImageSlot = {
  src: Templatable;
  /** Alt text for this image. Blank → ImageConfig.alt → the "Image Alt Text" rule → Title. */
  alt?: Templatable;
};

export type ImageConfig = {
  /** Source headers, in order, that hold image URLs. Blank cells skipped. Ignored when `slots` is set. */
  sourceColumns: string[];
  /** "dropbox-direct" rewrites dropbox.com share links to dl.dropboxusercontent.com. "none" passes through. */
  transform: "dropbox-direct" | "none";
  /**
   * Output columns whose (already-resolved) image URLs are appended to the
   * image set before fanout, after the source-column images.
   */
  outputColumns?: string[];
  /**
   * Ordered images (order = Image Position), each with an optional alt text.
   * When present (even empty) it replaces `sourceColumns`. Written by the
   * Images editor; older templates without it keep using `sourceColumns`.
   */
  slots?: ImageSlot[];
  /** Default alt text for every image (template). Blank → "Image Alt Text" rule → Title. */
  alt?: Templatable;
};

/** Profile-level defaults applied when a field rule is missing or evaluates to empty. */
export type ProfileDefaults = Partial<{
  vendor: string;
  status: string;
  published: string;
  weightUnit: string;
}> & Record<string, string>;

/**
 * A single taxonomy-attribute output field. Each enabled attribute of the
 * selected Shopify category becomes a Shopify product metafield column
 * (`<Name> (product.metafields.shopify.<handle>)`, or a reused fixed column).
 * The value is produced by a normal field Rule — typically a `static` pick
 * from Shopify's controlled vocabulary (or a custom value), but `source` /
 * `template` mapping from the sheet is also allowed.
 */
export type TaxonomyAttributeField = {
  /** Shopify attribute handle, e.g. "pattern" (metafield key shopify.<handle>). */
  handle: string;
  /** Display name, e.g. "Pattern" — the column-label prefix. */
  name: string;
  /** Whether this attribute is emitted as an output column. */
  enabled: boolean;
  /** How the cell value is produced (reuses the field Rule union). */
  rule: Rule;
};

/**
 * Product-taxonomy selections for a profile. Category names are stored as the
 * FULL PATH (e.g. "Apparel & Accessories > Clothing > … > Saris & Lehengas"),
 * never the id — that path is the value written to the CSV.
 *
 * Resolved into synthetic field rules + extra columns by
 * `src/lib/mapper/taxonomy-fields.ts`.
 */
export type TaxonomyConfig = {
  /** Selected Shopify taxonomy category. */
  shopify?: { categoryId: string; categoryName: string };
  /** Selected Google product-taxonomy category. */
  google?: { categoryId: string; categoryName: string };
  /** Output fields for the selected Shopify category's attributes. */
  attributes?: TaxonomyAttributeField[];
};

/** A complete mapping profile. Stored as MappingProfile.config (JSON). */
export type Profile = {
  productType: ProductType;
  defaults?: ProfileDefaults;
  /** Key is a Shopify column header (one of SHOPIFY_COLUMNS from export.ts). */
  fields: Record<string, Rule>;
  variants?: VariantConfig;
  images?: ImageConfig;
  /** Google + Shopify product-taxonomy category/attribute selections. */
  taxonomy?: TaxonomyConfig;
  /**
   * Manual priority within an independent tier of the dependency graph.
   * Columns listed here run first in their own tier; remaining columns run
   * in alphabetic order. Cross-tier order is dictated by `{{out:Field}}` deps
   * and CANNOT be overridden (a Body rule that reads `{{out:Title}}` always
   * runs after Title — manual sequence affects only sibling order, not deps).
   */
  executionOrder?: string[];
};

// ---------------------------------------------------------------------------
// Helper function signature — used by template.expand and mapping.applyProfile.
// ---------------------------------------------------------------------------
export type HelperFn = (value: string, ...args: string[]) => string;
export type HelperRegistry = Record<string, HelperFn>;

// ---------------------------------------------------------------------------
// Value Mapper — global named lookup dictionaries (rani pink → Pink).
// Stored in LookupDictionary (DB); loaded into resolution as a slug→dict map.
// ---------------------------------------------------------------------------
export type LookupEntry = { from: string; to: string };
export type LookupDictionary = {
  slug: string;
  name: string;
  entries: LookupEntry[];
  /** Match `from` ignoring case. Defaults to true. */
  caseInsensitive?: boolean;
  /** Trim whitespace on both the input and each `from` before comparing. Defaults to true. */
  trim?: boolean;
};

// ---------------------------------------------------------------------------
// Output row — a single row of the Shopify CSV.
// Key is a Shopify column header; value is the stringified cell.
// Empty cells must still be present as "" so export.ts can write a stable column order.
// ---------------------------------------------------------------------------
export type ShopifyRow = Record<string, string>;

// ---------------------------------------------------------------------------
// Result of applyProfile() — N rows per source row (variants × images).
// ---------------------------------------------------------------------------
export type ApplyResult = {
  rows: ShopifyRow[];
  /** Non-fatal issues (e.g., missing required field, unknown helper). Surfaced in preview UI. */
  warnings: string[];
};
